import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Form, Button, Card, Alert, Spinner } from "react-bootstrap";
import PermissionWrapper from "../components/PermissionWrapper";
import { getLoggedInUser } from "../services/UserService";
import { getTicketOptions } from "../services/BookingService";
import { createBookingEnquiry } from "../services/TicketService";
import { RootState } from "../states/rootReducer";

const BookingPage: React.FC = () => {
  const { isAuthenticated, userSession } = useSelector((state: RootState) => state.auth);
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [ticketOptions, setTicketOptions] = useState<any[]>([]);
  const [bookingData, setBookingData] = useState({
    scheduleId: '',
    phoneNumber: '',
    customerName: '',
    tickets: [] as { ticketOptionId: string; quantity: number }[]
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserDetails();
    }
  }, [isAuthenticated]);

  const fetchUserDetails = async () => {
    try {
      const user = await getLoggedInUser();
      setUserData(user);
      
      const customerName = user.firstName && user.lastName 
        ? `${user.firstName} ${user.lastName}` 
        : user.username || '';
      
      setBookingData(prev => ({
        ...prev,
        phoneNumber: user.phone || '',
        customerName: customerName
      }));
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const handleInputChange = async (field: string, value: string) => {
    setBookingData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'scheduleId' && value.trim()) {
      try {
        const options = await getTicketOptions(value.trim());
        setTicketOptions(options);
        setBookingData(prev => ({ ...prev, tickets: [] }));
      } catch (error) {
        setTicketOptions([]);
      }
    } else if (field === 'scheduleId' && !value.trim()) {
      setTicketOptions([]);
      setBookingData(prev => ({ ...prev, tickets: [] }));
    }
  };

  const updateTicketQuantity = (ticketOptionId: string, quantity: number) => {
    setBookingData(prev => {
      const existingIndex = prev.tickets.findIndex(t => t.ticketOptionId === ticketOptionId);
      
      if (quantity === 0) {
        return {
          ...prev,
          tickets: prev.tickets.filter(t => t.ticketOptionId !== ticketOptionId)
        };
      }
      
      if (existingIndex >= 0) {
        const updatedTickets = [...prev.tickets];
        updatedTickets[existingIndex].quantity = quantity;
        return { ...prev, tickets: updatedTickets };
      } else {
        return {
          ...prev,
          tickets: [...prev.tickets, { ticketOptionId, quantity }]
        };
      }
    });
  };

  const calculateTotal = () => {
    return bookingData.tickets.reduce((total, ticket) => {
      const option = ticketOptions.find(opt => opt._id === ticket.ticketOptionId);
      return total + (option ? option.price * ticket.quantity : 0);
    }, 0);
  };

  const getTicketQuantity = (ticketOptionId: string) => {
    const ticket = bookingData.tickets.find(t => t.ticketOptionId === ticketOptionId);
    return ticket ? ticket.quantity : 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const result = await createBookingEnquiry(bookingData);
      setMessage({ type: 'success', text: `Booking created! ID: ${result._id}` });
      setBookingData({
        scheduleId: '',
        phoneNumber: userData?.phone || '',
        customerName: userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.username : '',
        tickets: []
      });
      setTicketOptions([]);
    } catch (error: any) {
      setMessage({ type: 'error', text: 'Failed to create booking' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PermissionWrapper pageName="BookingPage">
      <div className="container mt-4">
        <h2>Book Tickets</h2>
        
        {message && (
          <Alert variant={message.type === 'success' ? 'success' : 'danger'}>
            {message.text}
          </Alert>
        )}

        <Card>
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Schedule ID</Form.Label>
                <Form.Control
                  type="text"
                  value={bookingData.scheduleId}
                  onChange={(e) => handleInputChange('scheduleId', e.target.value)}
                  required
                  placeholder="Enter schedule ID"
                />
              </Form.Group>

              {ticketOptions.length > 0 && (
                <Card className="mb-3">
                  <Card.Header><h5>Select Tickets</h5></Card.Header>
                  <Card.Body>
                    {ticketOptions.map(option => (
                      <div key={option._id} className="row mb-3 align-items-center">
                        <div className="col-md-6">
                          <strong>{option.ticketName}</strong>
                          <div className="text-muted">₹{option.price} each</div>
                          <small className="text-success">{option.availableSeats} available</small>
                        </div>
                        <div className="col-md-3">
                          <div className="input-group">
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => updateTicketQuantity(option._id, Math.max(0, getTicketQuantity(option._id) - 1))}
                            >
                              -
                            </button>
                            <input
                              type="number"
                              className="form-control text-center"
                              min="0"
                              max={option.availableSeats}
                              value={getTicketQuantity(option._id)}
                              onChange={(e) => updateTicketQuantity(option._id, parseInt(e.target.value) || 0)}
                            />
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => updateTicketQuantity(option._id, Math.min(option.availableSeats, getTicketQuantity(option._id) + 1))}
                            >
                              +
                            </button>
                          </div>
                        </div>
                        <div className="col-md-3 text-end">
                          <strong>₹{option.price * getTicketQuantity(option._id)}</strong>
                        </div>
                      </div>
                    ))}
                    
                    {bookingData.tickets.length > 0 && (
                      <>
                        <hr />
                        <div className="text-end">
                          <h5>Total: ₹{calculateTotal()}</h5>
                        </div>
                      </>
                    )}
                  </Card.Body>
                </Card>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Customer Name</Form.Label>
                <Form.Control
                  type="text"
                  value={bookingData.customerName}
                  onChange={(e) => handleInputChange('customerName', e.target.value)}
                  required
                  readOnly={isAuthenticated && userData}
                  className={isAuthenticated && userData ? 'bg-light' : ''}
                />
                {isAuthenticated && userData && (
                  <Form.Text className="text-muted">
                    Auto-filled from profile
                    <Button size="sm" variant="link" onClick={fetchUserDetails}>
                      Refresh
                    </Button>
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  type="tel"
                  value={bookingData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  required
                  readOnly={isAuthenticated && userData}
                  className={isAuthenticated && userData ? 'bg-light' : ''}
                />
                {isAuthenticated && userData && (
                  <Form.Text className="text-muted">Auto-filled from profile</Form.Text>
                )}
              </Form.Group>

              <Button 
                type="submit" 
                variant="primary" 
                disabled={loading || bookingData.tickets.length === 0}
                className="w-100"
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Creating...
                  </>
                ) : (
                  `Book Tickets - ₹${calculateTotal()}`
                )}
              </Button>
              
              {bookingData.tickets.length === 0 && (
                <small className="text-muted d-block mt-2 text-center">
                  Select tickets to proceed
                </small>
              )}
            </Form>
          </Card.Body>
        </Card>
      </div>
    </PermissionWrapper>
  );
};

export default BookingPage;